import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, User as UserIcon, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { profileService } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { ROUTES } from '@/constants';

export default function OnboardingPage() {
  const { user, profile, refetch } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState(profile?.display_name || '');
  const [bio, setBio] = useState(profile?.bio || '');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(profile?.avatar_url || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (profile?.avatar_url) setAvatarPreview(profile.avatar_url);
  }, [profile]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setAvatarFile(file);
    if (file) setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    setLoading(true);
    try {
      let avatarUrl = profile?.avatar_url;
      if (avatarFile && avatarPreview) {
        const { data, error } = await profileService.uploadAvatar(user.id, avatarFile);
        if (error) throw error;
        avatarUrl = data as string;
      }

      await profileService.updateProfile(user.id, {
        display_name: displayName || null,
        bio: bio || null,
        avatar_url: avatarUrl,
      });
      await refetch();
      toast('Profile setup complete');
      navigate(ROUTES.HOME);
    } catch (err: any) {
      toast('Setup failed', err.message, 'destructive');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-950 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md glass-panel rounded-3xl p-8 shadow-2xl"
      >
        <div className="text-center mb-8">
          <div className="h-12 w-12 rounded-2xl bg-brand-600 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4">K</div>
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Set up your profile</h2>
          <p className="text-neutral-500 dark:text-neutral-400 mt-2">Tell us a little about yourself</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col items-center">
            <div className="relative mb-4">
              <div className={cn('h-24 w-24 rounded-full overflow-hidden border-4 border-neutral-200 dark:border-neutral-700 mb-2', avatarPreview ? 'border-brand-600' : '')}>
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" className="object-cover w-full h-full" />
                ) : (
                  <div className="flex items-center justify-center h-full w-full text-neutral-400 bg-neutral-100 dark:bg-neutral-800">
                    <UserIcon size={32} />
                  </div>
                )}
              </div>
              <button
                onClick={() => document.getElementById('avatar-input')!.click()}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors"
              >
                <Upload size={16} /> Change photo
              </button>
              <input
                id="avatar-input"
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />
            </div>
          </div>

          <Input
            label="Display name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="e.g. Jane Doe"
          />
          <textarea
            value={bio}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setBio(e.target.value)}
            placeholder="What's on your mind?"
            className="input-field w-full min-h-[80px] border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-4 py-2.5 text-sm text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            rows={3}
          />

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? 'Setting up...' : 'Continue'}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}

import { cn } from '@/lib/cn';
